import React from "react";
import { Box } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { FloorplanResult, UnitResult } from "../types";
import {
  ExpandedUnitsWrap,
  FloorplanRowWrap,
  FloorplanThumb,
  FloorplanRowHeader,
  RowActions,
  RowTitle,
  SpecialRibbon,
  StatCell,
} from "../Availability.style";
import { FloorplanPlaceholder } from "./FloorplanPlaceholder";
import { UnitInlineRow } from "./UnitInlineRow";
import { currency, formatBedsBaths, shortDate } from "../format";
import { Button } from "@mui/material";

interface Props {
  floorplan: FloorplanResult;
  units: UnitResult[];
  expanded: boolean;
  onToggle: () => void;
  onApply: (unit: UnitResult) => void;
}

const pickPrimary = (units: UnitResult[]): UnitResult | null => {
  if (units.length === 0) return null;
  return [...units].sort((a, b) => {
    const ra = a.minEffectiveRent || Infinity;
    const rb = b.minEffectiveRent || Infinity;
    return ra - rb;
  })[0];
};

export const FloorplanRow: React.FC<Props> = ({
  floorplan,
  units,
  expanded,
  onToggle,
  onApply,
}) => {
  const primary = pickPrimary(units);
  const photo =
    floorplan.images.find((i) => i.type === "Floorplan") ?? floorplan.images[0];

  // No matching units - show floorplan summary row
  if (!primary) {
    return (
      <FloorplanRowWrap>
        {floorplan.hasSpecial && <SpecialRibbon>SPECIAL</SpecialRibbon>}
        <FloorplanRowHeader>
          <FloorplanThumb>
            {photo ? (
              <img src={photo.src} alt={floorplan.name} />
            ) : (
              <FloorplanPlaceholder />
            )}
          </FloorplanThumb>
          <RowTitle>
            <span className="title">
              {formatBedsBaths(floorplan.bedrooms, floorplan.bathrooms)}
            </span>
            <span className="subtitle">
              {floorplan.unitType} · {floorplan.name}
            </span>
            <span className="last">
              No units available for selected filters
            </span>
          </RowTitle>
          <StatCell>
            <span className="label">Base Rent</span>
            <span className="value">
              {floorplan.baseMinEffectiveRent
                ? `${currency(floorplan.baseMinEffectiveRent)} - ${currency(
                    floorplan.baseMaxEffectiveRent
                  )}`
                : "—"}
            </span>
          </StatCell>
          <StatCell>
            <span className="label">Sq Ft</span>
            <span className="value">
              {floorplan.minSqft === floorplan.maxSqft
                ? floorplan.minSqft
                : `${floorplan.minSqft}–${floorplan.maxSqft}`}
            </span>
          </StatCell>
          <Box />
          <Box />
          <Box />
          <Box />
          <RowActions>
            <Button variant="outlined" size="small" disabled>
              No Match
            </Button>
          </RowActions>
        </FloorplanRowHeader>
      </FloorplanRowWrap>
    );
  }

  const otherUnits = units.filter((u) => u.unitId !== primary.unitId);
  const otherCount = otherUnits.length;

  return (
    <FloorplanRowWrap>
      {floorplan.hasSpecial && <SpecialRibbon>SPECIAL</SpecialRibbon>}
      <FloorplanRowHeader>
        <FloorplanThumb>
          {photo ? (
            <img src={photo.src} alt={floorplan.name} />
          ) : (
            <FloorplanPlaceholder />
          )}
        </FloorplanThumb>

        <RowTitle>
          <span className="title">
            {formatBedsBaths(primary.bedrooms, primary.bathrooms)}
          </span>
          <span className="subtitle">
            Unit {primary.unitNumber} · {primary.unitType}
          </span>
          {otherCount === 0 ? (
            <span className="last">Last Available</span>
          ) : (
            <button className="expand" type="button" onClick={onToggle}>
              {expanded ? (
                <ExpandLessIcon fontSize="inherit" />
              ) : (
                <ExpandMoreIcon fontSize="inherit" />
              )}
              {otherCount} Other Available Unit{otherCount === 1 ? "" : "s"}
            </button>
          )}
        </RowTitle>

        <StatCell>
          <span className="label">Base Rent</span>
          <span className="value">
            {primary.minEffectiveRent
              ? currency(primary.minEffectiveRent)
              : "—"}
          </span>
        </StatCell>
        <StatCell>
          <span className="label">Sq Ft</span>
          <span className="value">
            {primary.minSqft === primary.maxSqft
              ? primary.minSqft
              : `${primary.minSqft}–${primary.maxSqft}`}
          </span>
        </StatCell>
        <StatCell>
          <span className="label">Available</span>
          <span className="value">{shortDate(primary.madeReadyDate)}</span>
        </StatCell>
        <StatCell>
          <span className="label">Term</span>
          <span className="value">
            {primary.matchedTerms[0]
              ? `${primary.matchedTerms[0].term} Months`
              : "—"}
          </span>
        </StatCell>
        <StatCell>
          <span className="label">Deposit</span>
          <span className="value">
            {primary.depositAmount > 0 ? currency(primary.depositAmount) : "—"}
          </span>
        </StatCell>
        <StatCell>
          <span className="label">Floor</span>
          <span className="value">{primary.floorLevel ?? "—"}</span>
        </StatCell>

        <RowActions>
          <Button
            variant="contained"
            size="small"
            onClick={() => onApply(primary)}
          >
            Apply
          </Button>
        </RowActions>
      </FloorplanRowHeader>

      {expanded && otherUnits.length > 0 && (
        <ExpandedUnitsWrap>
          {otherUnits.map((u) => (
            <UnitInlineRow
              key={u.unitId}
              unit={u}
              floorplan={floorplan}
              onApply={onApply}
              showThumb={false}
            />
          ))}
        </ExpandedUnitsWrap>
      )}
    </FloorplanRowWrap>
  );
};
